import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { Box, Button, Typography } from '@material-ui/core'
import { ImportHourSeriesDialog } from '../project-hour-series/ImportHourSeriesDialog'
import { useHourSeriesCollections } from '../../hour-series-collection/use-hour-series-collections'
import { HourSeriesCollectionList } from '../project-hour-series/HourSeriesCollectionList'
import {
  HourSeriesCollectionFragment,
  HourSeriesType,
  useGlobalHourSeriesCollectionsQuery,
} from '../../generated/graphql'
import { Loader } from '../common/Loader'

export function BuildingTemplatesPage(): React.ReactElement {
  const { t } = useTranslation()

  const [isImportingHourSeries, setIsImportingHourSeries] = useState(false)
  const [collections, setCollections] = useState<
    HourSeriesCollectionFragment[]
  >([])

  const { data, loading: isListLoading } = useGlobalHourSeriesCollectionsQuery()

  useEffect(() => {
    setCollections(
      data?.globalHourSeriesCollections.filter(
        (collection) =>
          !collection.hourSeries.some(
            (hourSeries) =>
              hourSeries.type === HourSeriesType.OutdoorTemperature,
          ),
      ) ?? [],
    )
  }, [data])

  const {
    createGlobalHourSeriesCollections,
    updateHourSeriesCollection,
    deleteHourSeriesCollection,
    isLoading: isCreateCollectionLoading,
  } = useHourSeriesCollections()

  const onCreateCollections = async (file: File, shouldOverwrite: boolean) => {
    await createGlobalHourSeriesCollections(file, shouldOverwrite)
  }

  return (
    <Box>
      <Helmet title={t('buildingTemplates.header')} />

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h3">{t('buildingTemplates.header')}</Typography>

        <Button
          id="import-building-template-button"
          variant="contained"
          color="primary"
          disabled={isListLoading}
          onClick={() => setIsImportingHourSeries(true)}
        >
          {t('buildingTemplates.createNew')}
        </Button>
      </Box>

      {isListLoading ? (
        <Loader />
      ) : (
        <HourSeriesCollectionList
          collections={collections}
          onDeleteCollection={async (collection) => {
            await deleteHourSeriesCollection(collection)
          }}
          onDeleteHourSeries={async (collection, deletedHourSeries) => {
            await updateHourSeriesCollection(collection.id, {
              hourSeriesIds: collection.hourSeries
                .map((hourSeries) => hourSeries.id)
                .filter((id) => id !== deletedHourSeries.id),
            })
          }}
        />
      )}

      <ImportHourSeriesDialog
        isOpen={isImportingHourSeries}
        isLoading={isCreateCollectionLoading}
        onCreateCollections={onCreateCollections}
        onClose={() => setIsImportingHourSeries(false)}
      />
    </Box>
  )
}
